/**
 * docChangedNotifier extension (OMD-233) — PM doc 변경을 참조 비교(O(1))로 감지하여
 * `NgwEditorLifecycle.notifyDocChanged()`(= handleEditorChange)를 발화한다.
 *
 * 배경: 어댑터가 `<Editor onChange>` prop 을 전달하면 라이브러리 `EditorCore.handleTransaction`
 * 이 매 docChanged 트랜잭션마다 `getHTML()`(전체 문서 DOMSerializer 직렬화) + `doc.toJSON()`
 * 을 eager 계산해 payload 로 넘긴다. 어댑터는 payload 를 사용하지 않는데도 2MB 본문(긴 답장
 * 체인)에서 키 입력당 ~1.15초가 직렬화에 소모됐다 (실측 input duration 1,186ms → 본 extension
 * 전환 후 37.9ms). 그래서 onChange prop 을 제거하고 본 plugin 이 변경 신호만 전달한다.
 *
 * 게이트 조건의 동치성: `prevState.doc !== state.doc` ⇔ `transaction.docChanged`.
 * PM 에서 docChanged 는 `steps.length > 0` 이고 addStep 은 항상 새 doc 객체를 할당하며,
 * step 없는 트랜잭션은 doc 참조를 유지한다. `reconfigure()` 도 doc 동일성을 보존하므로
 * plugin 주입/재구성 시 오탐이 없다.
 *
 * 타이밍: 기존 onChange 는 `view.updateState()` 반환 후 발화했지만 plugin view 의 update 는
 * updateState 내부(updatePluginViews — doc DOM 반영·selection 동기화 이후)에서 발화한다.
 * handleEditorChange 가 읽는 bridgeView 상태와 ensureQuotedToggle 의 DOM 조회 모두 이 시점에
 * 최신 상태를 본다 (openspec/changes/omd-233-large-mail-typing-lag/design.md §4).
 *
 * 구현 패턴은 `imageSelection` / `imageDpiAutoSize` 와 동일 — `@ngweditor/react` 가 PM
 * `Plugin` 을 runtime export 하지 않으므로 `view.state.plugins[0].constructor`(BundledPlugin)
 * 로 plugin view 인스턴스를 생성해 주입.
 */

import type { EditorState, EditorView, NgwExtension, Plugin } from '@ngweditor/react'
import type { NgwEditorAdapter } from '../NgwEditorAdapter'

/** BundledPlugin duck-typing — plugin view 의 update 로 doc 변경을 감지. */
interface DocChangedPluginSpec {
  view?: (view: EditorView) => {
    update: (view: EditorView, prevState: EditorState) => void
    destroy?: () => void
  }
}

export function createDocChangedNotifierExtension(adapter: NgwEditorAdapter): NgwExtension {
  return {
    name: 'doc-changed-notifier',
    setup(ctx) {
      const view = ctx.editor.view
      const firstPlugin = view.state.plugins[0]
      const BundledPlugin = firstPlugin?.constructor as
        | (new (spec: DocChangedPluginSpec) => Plugin)
        | undefined
      if (BundledPlugin == null) {
        return
      }

      const notifierPlugin = new BundledPlugin({
        view: () => ({
          update: (pluginView, prevState) => {
            if (prevState != null && pluginView.state.doc !== prevState.doc) {
              adapter.lifecycle.notifyDocChanged()
            }
          },
        }),
      })

      view.updateState(
        view.state.reconfigure({
          plugins: [...view.state.plugins, notifierPlugin],
        }),
      )
    },
  }
}
